import React, { FunctionComponent, useState } from "react";
import {
  Grid,
  Typography,
  TextField,
  Fab,
  Button,
  Tooltip,
} from "@material-ui/core";
import AddCircleOutlineIcon from "@material-ui/icons/AddCircleOutline";
import DeleteIcon from "@material-ui/icons/Delete";
import { Formik, Form, ErrorMessage } from "formik";
import * as yup from "yup";

import { useConfigStyles } from "../../configStyles";
import { useStore } from "../../../../store";
import { SnackBar } from "../../../SnackBar";
import { getDecryptedData } from "../../../../utils/crypto";

interface PosConfigProps {
  editFlag: boolean;
  editData: any;
  goBackToList: () => void;
}
export const StoreConfiguration: FunctionComponent<PosConfigProps> = ({
  editFlag,
  editData,
  goBackToList,
}) => {
  const classes = useConfigStyles();
  const [data, setData] = useState<Array<object>>([
    {
      id: 0,
      name: "",
      config_value: "",
    },
  ]);
  const [snackbarColor, setSnackbarColor] = useState<string>("#d50000");
  const [storeConfigStatus, setStoreConfigStatus] = useState<string>("");
  const [submitFlag, setSubmitFlag] = useState<boolean>(false);
  const [
    storeConfigurations,
    {
      getStoreConfigurations,
      createStoreConfiguration,
      updateStoreConfiguration,
    },
  ] = useStore("storeConfigurations");

  const initialValues = {
    name: "",
    config_value: "",
  };

  if (editFlag === true) {
    initialValues.name = editData.name;
    initialValues.config_value =
      editData.name.slice(-3).toLowerCase() === "pwd"
        ? getDecryptedData(editData.config_value)
        : editData.config_value;
    data.map((i: any) => {
      i.id = editData.id;
      return i;
    });
  }
  const storeConfigSchema = yup.object().shape({
    name: yup.string().required("Name is required"),
    config_value: yup.string().required("Config Value is required"),
  });

  const addRow = (values: any, id: number) => {
    const available =
      storeConfigurations &&
      storeConfigurations.find(
        (i: any) => i.name.toLowerCase() === values.name.toLowerCase()
      );

    const filteredData = data && data.filter((i: any) => i.id !== id);
    if (
      available === undefined &&
      filteredData.find(
        (i: any) => i.name.toLowerCase() === values.name.toLowerCase()
      ) === undefined
    ) {
      const newData = {
        id: id + 1,
        name: "",
        config_value: "",
      };
      const dataArray = [...data, newData];
      setData(dataArray);
    } else {
      setSnackbarColor("red");
      setStoreConfigStatus("Name already exists");
    }
  };

  const deleteRow = (id: number) => {
    const filteredArray = data.filter((i: any) => i.id !== id);
    setData(filteredArray);
  };

  const validateAll = (dataArray: any, values: any) => {
    const filteredStoreData =
      storeConfigurations &&
      storeConfigurations.filter((i: any) => i.name !== values.name);
    const names =
      editFlag === true
        ? filteredStoreData &&
          filteredStoreData.map((i: any) => i.name.toLowerCase())
        : storeConfigurations &&
          storeConfigurations.map((i: any) => i.name.toLowerCase());

    const dataNames =
      dataArray && dataArray.map((i: any) => i.name.toLowerCase());

    const availableData = dataNames.map((i: any) => {
      if (names.includes(i)) {
        return true;
      } else {
        return false;
      }
    });

    const isNameExist =
      editFlag === true
        ? values.name === ""
          ? values.name
          : undefined
        : dataArray.find((i: any) => i.name === "");

    const isConfigValueExist =
      editFlag === true
        ? values.config_value === ""
          ? values.config_value
          : undefined
        : dataArray.find((i: any) => i.config_value === "");

    if (availableData.includes(true)) {
      setSnackbarColor("red");
      setStoreConfigStatus("Name already exists");
    } else if (isNameExist !== undefined) {
      setSnackbarColor("red");
      setStoreConfigStatus("Name is required");
    } else if (isConfigValueExist !== undefined) {
      setSnackbarColor("red");
      setStoreConfigStatus("Config Value is required");
    } else {
      submitAll(dataArray, values);
    }
  };

  const handleSubmit = (id: number, values: any) => {
    validateAll(data, values);
  };

  const submitAll = async (data: any, values: any) => {
    try {
      const filteredArray = data.filter((i: any) => delete i.id);
      if (editFlag === true) {
        await updateStoreConfiguration(editData.id, values);
        setSnackbarColor("green");
        setStoreConfigStatus("Store Configuration successfully updated");
        setTimeout(() => {
          getStoreConfigurations();
          goBackToList();
        }, 2000);
      } else {
        await createStoreConfiguration(filteredArray);
        setSnackbarColor("green");
        setStoreConfigStatus("Store Configuration successfully created");
        setTimeout(() => {
          getStoreConfigurations();
          goBackToList();
        }, 2000);
      }
      setSubmitFlag(false);
      setData([
        {
          id: 0,
          name: "",
          config_value: "",
        },
      ]);
    } catch (error:any) {
      console.log(error);
      setSnackbarColor("red");
      setStoreConfigStatus(error);
    }
  };

  const checkAvailability = (name: string, id: string) => {
    const available =
      storeConfigurations &&
      storeConfigurations.find(
        (i: any) => i.name.toLowerCase() === name.toLowerCase()
      );
    const filteredData = data && data.filter((i: any) => i.id !== id);
    if (
      available !== undefined ||
      filteredData.find(
        (i: any) => i.name.toLowerCase() === name.toLowerCase()
      ) !== undefined
    ) {
      setSnackbarColor("red");
      setStoreConfigStatus("Name already exists");
    }
  };

  const onChangeHandle = (newName: string, id: number, type: string) => {
    if (type === "name") {
      data.map((i: any) => {
        if (i.id === id) {
          i.name = newName;
          return i;
        }
      });
    } else if (type === "config_value") {
      data.map((i: any) => {
        if (i.id === id) {
          i.config_value = newName;
          return i;
        }
      });
    }
  };

  const onCloseSnackbar = () => {
    setStoreConfigStatus("");
  };

  return (
    <div>
      <SnackBar
        status={storeConfigStatus}
        onCloseError={onCloseSnackbar}
        color={snackbarColor}
      />
      <Grid
        container
        spacing={2}
        direction="row"
        justify="space-between"
        alignItems="center"
      >
        <Grid item xs={12}>
          <Typography className={classes.configTitle}>
            Store Configuration
          </Typography>
        </Grid>

        <Grid item xs={12}>
          {data.map((i: any) => (
            <Grid container className={classes.mainGridStyle}>
              <Grid item>
                <Formik
                  enableReinitialize
                  validationSchema={storeConfigSchema}
                  initialValues={initialValues}
                  onSubmit={(values) => {
                    addRow(values, i.id);
                  }}
                >
                  {({ values, handleChange, setFieldValue, resetForm }) => (
                    <Form>
                      <Grid container direction="row">
                        <Grid item>
                          <Grid
                            container
                            spacing={5}
                            className={classes.fieldSpace}
                          >
                            <Grid item xs className={classes.textFieldStyles}>
                              {i === data[0] ? (
                                <Typography
                                  className={classes.fieldLabelStyles}
                                >
                                  Name
                                </Typography>
                              ) : null}
                              <TextField
                                variant="outlined"
                                name="name"
                                value={editFlag === true ? values.name : i.name}
                                onChange={(e) => {
                                  handleChange(e);
                                  onChangeHandle(e.target.value, i.id, "name");
                                  checkAvailability(e.target.value, i.id);
                                }}
                                fullWidth={true}
                                disabled={editFlag}
                              />

                              <ErrorMessage name="name">
                                {(msg) => (
                                  <div style={{ color: "red" }}>{msg}</div>
                                )}
                              </ErrorMessage>
                            </Grid>
                            <Grid item xs className={classes.textFieldStyles}>
                              {i === data[0] ? (
                                <Typography
                                  className={classes.fieldLabelStyles}
                                >
                                  Config Value
                                </Typography>
                              ) : null}

                              <TextField
                                variant="outlined"
                                name="config_value"
                                value={
                                  editFlag === true
                                    ? values.config_value
                                    : i.config_value
                                }
                                type={
                                  values.name.slice(-3).toLowerCase() === "pwd"
                                    ? "password"
                                    : "text"
                                }
                                onChange={(e) => {
                                  handleChange(e);
                                  onChangeHandle(
                                    e.target.value,
                                    i.id,
                                    "config_value"
                                  );
                                }}
                                fullWidth={true}
                              />

                              <ErrorMessage name="config_value">
                                {(msg) => (
                                  <div style={{ color: "red" }}>{msg}</div>
                                )}
                              </ErrorMessage>
                            </Grid>

                            <Grid item>
                              <Grid
                                container
                                direction="row"
                                style={{ marginTop: i === 0 ? "1em" : "-1em" }}
                              >
                                {editFlag === false ? (
                                  <Grid
                                    item
                                    className={classes.textFieldStyles}
                                  >
                                    <Tooltip title="Add">
                                      <Fab
                                        size="small"
                                        aria-label="add"
                                        color="primary"
                                        type="submit"
                                        style={{
                                          fontSize: "14px",
                                          marginTop: "2em",
                                        }}
                                        disabled={
                                          submitFlag === true ||
                                          data.length === 5
                                        }
                                      >
                                        <AddCircleOutlineIcon />
                                      </Fab>
                                    </Tooltip>
                                  </Grid>
                                ) : null}
                                {i.id !== 0 && editFlag === false ? (
                                  <Grid
                                    item
                                    className={classes.textFieldStyles}
                                  >
                                    <Tooltip title="Delete">
                                      <Fab
                                        size="small"
                                        aria-label="delete"
                                        color="primary"
                                        disabled={submitFlag === true}
                                        style={{
                                          fontSize: "14px",
                                          marginTop: "2em",
                                        }}
                                        onClick={() => deleteRow(i.id)}
                                      >
                                        <DeleteIcon />
                                      </Fab>
                                    </Tooltip>
                                  </Grid>
                                ) : null}
                              </Grid>
                            </Grid>
                          </Grid>
                        </Grid>

                        {i === data[data.length - 1] ? (
                          <Grid item xs={12} className={classes.buttonsStyles}>
                            <Grid
                              container
                              direction="row"
                              justify="flex-end"
                              spacing={10}
                            >
                              <Grid item xs={12}>
                                <Button
                                  variant="contained"
                                  color="primary"
                                  size="medium"
                                  onClick={() => handleSubmit(i, values)}
                                >
                                  Submit
                                </Button>
                                <Button
                                  variant="contained"
                                  color="primary"
                                  type="reset"
                                  size="medium"
                                  style={{
                                    marginLeft: "1em",
                                  }}
                                  onClick={() => {
                                    setData([
                                      {
                                        id: 0,
                                        name: "",
                                        config_value: "",
                                      },
                                    ]);
                                    setSubmitFlag(false);
                                    resetForm();
                                  }}
                                >
                                  Reset
                                </Button>
                              </Grid>
                            </Grid>
                          </Grid>
                        ) : null}
                      </Grid>
                    </Form>
                  )}
                </Formik>
              </Grid>
            </Grid>
          ))}
          {data.length === 5 ? (
            <Grid item xs={12} className={classes.buttonsStyles}>
              <Grid container direction="row">
                <Grid item xs={12}>
                  <label style={{ color: "red" }}>
                    Maximum 5 rows are allowed
                  </label>
                </Grid>
              </Grid>
            </Grid>
          ) : null}
        </Grid>
      </Grid>
    </div>
  );
};
